import React from "react";
import { Plus, Minus, FileText, ArrowRightLeft, CircleDot, BookOpen, X, Code2, ListChecks } from "lucide-react";
import { useTheme } from "../../../hooks/useTheme";
import ApexDescription from "../../common/ApexDescription";

const SectionLabel = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2.5 mb-3">
    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-blue-50 dark:bg-blue-500/10 flex-shrink-0">
      <Icon size={12} className="text-textHeading dark:text-blue-400" />
    </div>
    <span className="text-xs font-bold uppercase tracking-widest text-textHeading dark:text-blue-400">{title}</span>
  </div>
);

const Card = ({ children }) => (
  <div className="bg-white dark:bg-white/[0.03] rounded-2xl border border-gray-100 dark:border-white/[0.08] p-4 shadow-sm">
    {children}
  </div>
);

const ProblemDetailModal = ({ problem, action, onAction, onClose, loading = false }) => {
  const { theme } = useTheme();
  if (!problem) return null;

  const hasExamples = problem.examples?.length > 0;
  const hasBothFormats = problem.input_format && problem.output_format;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-bgPrimary/50 dark:bg-black/20"
        style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
        onClick={onClose}
      />

      {/* Modal card */}
      <div
        className="w-full max-w-xl flex flex-col max-h-[88vh] relative bg-bgSecondary rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 animate-[fadeInScale_0.2s_ease-out]"
        style={{ background: theme === "dark" ? "linear-gradient(to bottom right, rgba(15,18,42,0.94), rgba(10,9,28,0.90))" : "" }}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 dark:border-white/[0.07]">
          <div className="flex-1 mr-4">
            <h2
              className="text-base font-bold text-textPrimary dark:text-white leading-snug"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              {problem.title}
            </h2>
            <div className="flex items-center gap-0 flex-wrap mt-2">
              {problem.difficulty && (
                <span className={`inline-block text-[10px] font-bold px-2.5 py-0.5 rounded-full ${
                  problem.difficulty === "Easy"
                    ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                    : problem.difficulty === "Hard"
                    ? "bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400"
                    : "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400"
                }`}>
                  {problem.difficulty}
                </span>
              )}
              {problem.estimated_time && (
                <span className="inline-block ml-1.5 text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400">
                  {problem.estimated_time}m
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-rose-500 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-all flex-shrink-0"
          >
            <X size={15} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
          {loading ? (
            <div className="space-y-4">
              {[56, 72, 48, 100].map((h, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-gray-100 dark:bg-white/[0.08] animate-pulse"
                  style={{ height: `${h}px` }}
                />
              ))}
            </div>
          ) : (
            <>
              {/* Problem Statement */}
              {problem.description && (
                <Card>
                  <SectionLabel icon={FileText} title="Problem Statement" />
                  {problem.track === "apex" ? (
                    <div className="text-sm leading-relaxed">
                      <ApexDescription text={problem.description} textClassName="text-gray-600 dark:text-gray-300" codeClassName="bg-gray-900 text-green-300" />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      {problem.description}
                    </p>
                  )}
                </Card>
              )}

              {/* Apex: topics, starter code, grading rubric */}
              {problem.track === "apex" && (
                <>
                  {problem.topics?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {problem.topics.map((t) => (
                        <span key={t} className="text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-300 border border-violet-200 dark:border-violet-500/20">{t}</span>
                      ))}
                    </div>
                  )}
                  {problem.starter_code && (
                    <Card>
                      <SectionLabel icon={Code2} title="Starter Code" />
                      <pre className="font-mono text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap bg-gray-50 dark:bg-white/[0.04] rounded-xl border border-gray-100 dark:border-white/[0.06] px-4 py-3 overflow-x-auto">{problem.starter_code}</pre>
                    </Card>
                  )}
                  {problem.rubric?.length > 0 && (
                    <Card>
                      <SectionLabel icon={ListChecks} title="Grading Rubric (AI-reviewed)" />
                      <div className="space-y-2">
                        {problem.rubric.map((c) => (
                          <div key={c.id || c.name} className="flex items-start gap-3 rounded-xl bg-gray-50 dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.06] px-3 py-2.5">
                            <span className="flex-shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-300">{c.weight}%</span>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold dark:text-white text-textPrimary">{c.name}</p>
                              {c.description && <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-0.5">{c.description}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                  {problem.reference_solution && (
                    <Card>
                      <SectionLabel icon={Code2} title="Reference Solution (only you can see this)" />
                      <pre className="font-mono text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap bg-gray-50 dark:bg-white/[0.04] rounded-xl border border-gray-100 dark:border-white/[0.06] px-4 py-3 overflow-x-auto">{problem.reference_solution}</pre>
                    </Card>
                  )}
                </>
              )}

              {/* Input / Output Format */}
              {(problem.input_format || problem.output_format) && (
                <Card>
                  {hasBothFormats ? (
                    <div className="grid grid-cols-2">
                      <div className="pr-5">
                        <SectionLabel icon={ArrowRightLeft} title="Input Format" />
                        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                          {problem.input_format}
                        </p>
                      </div>
                      <div className="pl-5 border-l border-gray-100 dark:border-white/[0.08]">
                        <SectionLabel icon={ArrowRightLeft} title="Output Format" />
                        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                          {problem.output_format}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {problem.input_format && (
                        <>
                          <SectionLabel icon={ArrowRightLeft} title="Input Format" />
                          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                            {problem.input_format}
                          </p>
                        </>
                      )}
                      {problem.output_format && (
                        <>
                          <SectionLabel icon={ArrowRightLeft} title="Output Format" />
                          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                            {problem.output_format}
                          </p>
                        </>
                      )}
                    </>
                  )}
                </Card>
              )}

              {/* Constraints */}
              {problem.constraints && (
                <Card>
                  <SectionLabel icon={CircleDot} title="Constraints" />
                  <div className="relative overflow-hidden bg-gray-50 dark:bg-white/[0.04] rounded-xl border border-gray-100 dark:border-white/[0.06] px-4 py-3">
                    <div className="absolute left-0 inset-y-0 w-0.5 bg-blue-300 dark:bg-blue-500/50 rounded-full" />
                    <p className="pl-2 font-mono text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                      {problem.constraints}
                    </p>
                  </div>
                </Card>
              )}

              {/* Examples */}
              {hasExamples && (
                <Card>
                  <SectionLabel icon={BookOpen} title="Examples" />
                  <div className="space-y-3">
                    {problem.examples.map((ex, i) => (
                      <div
                        key={i}
                        className="rounded-xl bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.1] p-4"
                      >
                        <p className="text-[10px] font-bold uppercase tracking-wider text-blue-500 dark:text-blue-400 mb-3">
                          Example {i + 1}
                        </p>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 mb-1.5">
                              Input
                            </p>
                            <div className="relative overflow-hidden bg-white dark:bg-white/[0.06] rounded-lg border border-gray-100 dark:border-white/[0.08] px-3 py-2">
                              <div className="absolute left-0 inset-y-0 w-0.5 bg-blue-300 dark:bg-blue-400/50 rounded-full" />
                              <span className="pl-1 font-mono text-sm text-textPrimary dark:text-gray-200 leading-snug break-all">
                                {ex.input}
                              </span>
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 mb-1.5">
                              Output
                            </p>
                            <div className="relative overflow-hidden bg-white dark:bg-white/[0.06] rounded-lg border border-gray-100 dark:border-white/[0.08] px-3 py-2">
                              <div className="absolute left-0 inset-y-0 w-0.5 bg-emerald-300 dark:bg-emerald-400/50 rounded-full" />
                              <span className="pl-1 font-mono text-sm text-textPrimary dark:text-gray-200 leading-snug break-all">
                                {ex.output}
                              </span>
                            </div>
                          </div>
                        </div>
                        {ex.explanation && (
                          <div className="flex gap-2.5 pt-2.5 border-t border-gray-100 dark:border-white/[0.06]">
                            <div className="w-0.5 flex-shrink-0 bg-gray-200 dark:bg-white/10 rounded-full mt-0.5" />
                            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed italic">
                              {ex.explanation}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Empty state */}
              {!problem.description && !hasExamples && !problem.constraints && (
                <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
                  <BookOpen size={28} className="text-gray-200 dark:text-white/10" />
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    No details available for this problem.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 dark:border-white/10">
          <button
            onClick={onClose}
            className="border-btn dark:border-btn-dark px-5 py-2.5 rounded-full text-sm font-medium transition-all"
          >
            Close
          </button>
          {action === "add" ? (
            <button
              onClick={() => { onAction(problem); onClose(); }}
              className="button flex items-center gap-2 px-5 py-2.5"
            >
              <Plus size={14} /><span>Add to Set</span>
            </button>
          ) : (
            <button
              onClick={() => { onAction(problem); onClose(); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold transition-all"
            >
              <Minus size={14} /><span>Remove from Set</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProblemDetailModal;
